import { credentialFetch } from "../auth/credential-fetch";
import { deviceToken } from "../identity/device-token";
import {
  deleteAcknowledgedUsageEvents,
  getQueuedUsageEvents,
  putQueuedUsageEvent,
  rejectQueuedUsageEvents,
  type QueuedUsageEvent,
} from "../outbox/db";
import { transportConfigFromEnv, type TransportConfig } from "../outbox/http-transport";
import { cookieOwnerMatches } from "../outbox/runtime";
import { currentPwaRelease } from "./release-identity";

export type UsageEventKind = QueuedUsageEvent["event_kind"];
export type UsageSender = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface UsageClientIdentity {
  owner_principal: string;
  tenant_id: string;
  actor_id: string;
}

export interface UsageReport {
  contract_version: "1.0";
  timezone: string;
  as_of: string;
  windows: { dau: string; wau: string; mau: string };
  dau: number;
  wau: number;
  mau: number;
  active_minutes: number;
  category_counts: Record<string, number>;
  actors: Array<{
    actor_id: string;
    last_active: string;
    active_minutes: number;
    captures_saved: number;
    reports_filed: number;
    actions_changed: number;
    decisions_changed: number;
  }>;
}

export interface UsageReportClient {
  load(): Promise<UsageReport>;
  downloadCsv(): Promise<"downloaded">;
}

function eventId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const random = Math.random().toString(36).slice(2);
  return `evt-${Date.now().toString(36)}-${random}`;
}

export class UsageClient {
  private flushing: Promise<void> | null = null;
  private drainRequested = false;
  private stopped = false;
  private abortController: AbortController | null = null;

  constructor(
    private readonly config: TransportConfig,
    private readonly identity: UsageClientIdentity,
    private readonly send: UsageSender = credentialFetch,
    private readonly now: () => Date = () => new Date(),
    private readonly appRelease: () => string = currentPwaRelease,
    private readonly ownerMatches: (signal?: AbortSignal) => Promise<boolean> = async () => true,
  ) {}

  async track(
    event_kind: UsageEventKind,
    project_ref?: string | null,
    foreground_seconds?: number,
  ): Promise<string> {
    const client_event_id = eventId();
    const event: QueuedUsageEvent = {
      ...this.identity,
      delivery_state: "pending",
      client_event_id,
      event_kind,
      occurred_at: this.now().toISOString(),
      app_release: this.appRelease(),
      ...(project_ref ? { project_ref } : {}),
      ...(foreground_seconds === undefined ? {} : { foreground_seconds }),
    };
    await putQueuedUsageEvent(event);
    console.info("[mark-usage] queued=1");
    void this.flush();
    return client_event_id;
  }

  flush(): Promise<void> {
    if (this.stopped) return Promise.resolve();
    if (this.flushing) {
      this.drainRequested = true;
      return this.flushing;
    }
    this.drainRequested = false;
    let blocked = false;
    this.flushing = this.drain().then((result) => {
      blocked = result === "blocked";
    }).finally(() => {
      this.flushing = null;
      if (!blocked && this.drainRequested && !this.stopped) void this.flush();
    });
    return this.flushing;
  }

  stop(): void {
    this.stopped = true;
    this.abortController?.abort();
  }

  private async drain(): Promise<"drained" | "blocked"> {
    while (!this.stopped) {
      const events = await getQueuedUsageEvents(this.identity.owner_principal);
      if (events.length === 0) return "drained";
      this.abortController = new AbortController();
      try {
        if (!await this.ownerMatches(this.abortController.signal)) return "blocked";
      } catch {
        return "blocked";
      }
      if (this.stopped) return "blocked";
      const outcome = await this.sendEvents(events);
      if (outcome === "blocked") return "blocked";
    }
    return "blocked";
  }

  private async sendEvents(events: QueuedUsageEvent[]): Promise<"progress" | "blocked"> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (this.config.token) headers.Authorization = `Bearer ${this.config.token}`;
    this.abortController = new AbortController();
    let response: Response;
    try {
      response = await this.send(`${this.config.endpoint}/mark/usage/events`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({
          contract_version: "1.0",
          events: events.map(({ owner_principal: _owner, delivery_state: _state, rejection_code: _rejection, ...event }) => event),
        }),
        signal: this.abortController.signal,
      });
    } catch {
      return "blocked";
    }
    if (
      [401, 403, 404, 405, 408, 425, 429, 501].includes(response.status)
      || response.status >= 500
    ) {
      return "blocked";
    }
    if (!response.ok) {
      if (events.length > 1) {
        for (const event of events) {
          if (await this.sendEvents([event]) === "blocked") return "blocked";
        }
        return "progress";
      }
      await rejectQueuedUsageEvents(this.identity.owner_principal, [{
        client_event_id: events[0].client_event_id,
        error: `usage_contract_http_${response.status}`,
      }]);
      return "progress";
    }
    let results: Array<{
      client_event_id: string;
      status: "accepted" | "duplicate" | "rejected";
      error?: string;
    }>;
    try {
      const body = await response.json() as {
        status?: unknown;
        payload?: { results?: unknown };
      };
      if (body.status !== "accepted" || !Array.isArray(body.payload?.results)) {
        return "blocked";
      }
      results = body.payload.results as typeof results;
    } catch {
      return "blocked";
    }
    const submitted = new Set(events.map((event) => event.client_event_id));
    const seen = new Set<string>();
    for (const result of results) {
      if (
        !result
        || typeof result.client_event_id !== "string"
        || !submitted.has(result.client_event_id)
        || seen.has(result.client_event_id)
        || !["accepted", "duplicate", "rejected"].includes(result.status)
      ) return "blocked";
      seen.add(result.client_event_id);
    }
    if (results.some((result) =>
      result.status === "rejected" && result.error === "usage_scope_mismatch"
    )) return "blocked";
    const acknowledged = results
      .filter((result) => result.status === "accepted" || result.status === "duplicate")
      .map((result) => result.client_event_id);
    const rejected = results
      .filter((result) => result.status === "rejected" && typeof result.error === "string")
      .map((result) => ({ client_event_id: result.client_event_id, error: result.error! }));
    if (acknowledged.length === 0 && rejected.length === 0) return "blocked";
    await deleteAcknowledgedUsageEvents(this.identity.owner_principal, acknowledged);
    await rejectQueuedUsageEvents(this.identity.owner_principal, rejected);
    console.info(`[mark-usage] acknowledged=${acknowledged.length} rejected=${rejected.length}`);
    return "progress";
  }

  async load(): Promise<UsageReport> {
    // A report read must observe this owner's already-queued foreground/action
    // facts when the network is healthy. Without this barrier the admin view
    // can race the background drain and render a stale zero indefinitely.
    await this.flush();
    const response = await this.get("/mark/usage/report");
    if (!response.ok) throw new Error(`usage report refused (${response.status})`);
    const body = await response.json() as { status?: unknown; payload?: UsageReport };
    if (body.status !== "accepted" || !body.payload) throw new Error("usage report malformed");
    return body.payload;
  }

  async downloadCsv(): Promise<"downloaded"> {
    // Use the real authenticated attachment response. Android's installed-PWA
    // shell turns client-created blob URLs into a share flow even when an
    // anchor has `download`; the coordinator already supplies no-store plus a
    // dated Content-Disposition filename, which Chrome routes to Downloads.
    const anchor = document.createElement("a");
    anchor.href = `${this.config.endpoint}/mark/usage/report?format=csv`;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return "downloaded";
  }

  private get(path: string): Promise<Response> {
    const headers: Record<string, string> = {};
    if (this.config.token) headers.Authorization = `Bearer ${this.config.token}`;
    return this.send(`${this.config.endpoint}${path}`, {
      headers,
      credentials: "include",
    });
  }
}

export function makeUsageClient(
  identity: UsageClientIdentity,
  env: Record<string, string | undefined> = import.meta.env as unknown as Record<string, string | undefined>,
): UsageClient | null {
  if (!env.VITE_MARK_ENDPOINT) return null;
  const config = transportConfigFromEnv(env, deviceToken());
  return new UsageClient(
    config,
    identity,
    credentialFetch,
    () => new Date(),
    currentPwaRelease,
    (signal) => cookieOwnerMatches(config, identity.owner_principal, signal),
  );
}
